import React, { useContext } from 'react';
import { Table, Badge, Button, Card, Pagination, Spinner } from 'react-bootstrap';
import { formatDateTime } from '../../utils/dateFormatter';
import { AuthContext } from '../../context/AuthContext';
import './MessageQueueTable.css';

const MessageQueueTable = ({ messages, pagination, onPageChange, onCancel, onRetry, loading }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'قيد الانتظار' },
      processing: { variant: 'info', text: 'جاري الإرسال' },
      sent: { variant: 'success', text: 'تم الإرسال' },
      delivered: { variant: 'success', text: 'تم التوصيل' },
      failed: { variant: 'danger', text: 'فشل' },
      expired: { variant: 'secondary', text: 'منتهي' },
      cancelled: { variant: 'dark', text: 'ملغي' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getChannelIcon = (channel) => {
    if (channel === 'whatsapp') return '📱 واتساب';
    if (channel === 'sms') return '💬 SMS';
    return channel;
  };

  if (loading && messages.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">جاري تحميل الرسائل...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <i className="bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
          <p className="mt-3 text-muted">لا توجد رسائل</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="desktop-table" dir="rtl">
        <Table responsive hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th>المستلم</th>
              <th>القالب</th>
              <th>القناة</th>
              <th>الحالة</th>
              <th>موعد الإرسال</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((message) => (
              <tr key={message._id}>
                <td>
                  <div>
                    <strong>{message.recipient?.name || 'غير محدد'}</strong>
                    <br />
                    <small className="text-muted">{message.recipient?.phone}</small>
                  </div>
                </td>
                <td>
                  <div>
                    {message.template?.name?.ar || 'غير محدد'}
                    <br />
                    <small className="text-muted">
                      {message.template?.category === 'guest' ? 'رسالة ضيف' : 'رسالة موظف'}
                    </small>
                  </div>
                </td>
                <td>{getChannelIcon(message.channel)}</td>
                <td>{getStatusBadge(message.status)}</td>
                <td>
                  <small>{formatDateTime(message.scheduledFor)}</small>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    {message.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => onCancel(message._id)}
                      >
                        إلغاء
                      </Button>
                    )}
                    {message.status === 'failed' && isAdmin && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => onRetry(message._id)}
                      >
                        إعادة محاولة
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards" dir="rtl">
        {messages.map((message) => (
          <Card key={message._id} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <strong>{message.recipient?.name || 'غير محدد'}</strong>
                  <br />
                  <small className="text-muted">{message.recipient?.phone}</small>
                </div>
                {getStatusBadge(message.status)}
              </div>

              <div className="mb-2">
                <small className="text-muted">القالب:</small>
                <div>{message.template?.name?.ar || 'غير محدد'}</div>
              </div>

              <div className="mb-2">
                <small className="text-muted">القناة:</small>
                <div>{getChannelIcon(message.channel)}</div>
              </div>

              <div className="mb-3">
                <small className="text-muted">موعد الإرسال:</small>
                <div><small>{formatDateTime(message.scheduledFor)}</small></div>
              </div>

              {(message.status === 'pending' || (message.status === 'failed' && isAdmin)) && (
                <div className="d-flex gap-2">
                  {message.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => onCancel(message._id)}
                    >
                      إلغاء
                    </Button>
                  )}
                  {message.status === 'failed' && isAdmin && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => onRetry(message._id)}
                    >
                      إعادة محاولة
                    </Button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination dir="rtl">
            <Pagination.Prev
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              السابق
            </Pagination.Prev>

            {[...Array(pagination.pages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={index + 1 === pagination.page}
                onClick={() => onPageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              التالي
            </Pagination.Next>
          </Pagination>
        </div>
      )}
    </>
  );
};

export default MessageQueueTable;
